import { isToday, isYesterday, format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DayGroup } from './DayGroup';
import { EmptyAlertsState } from './EmptyAlertsState';
import { EmptyInsightsState } from './EmptyInsightsState';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { toValidDate } from '@/Domain/utils/safeDate';
import {
  InsightType,
  UnifiedInsightItem,
} from '@/UI/Insights/UnifiedAiInsights/hooks/useUnifiedInsights.hook';

interface InsightsSectionProps {
  items: UnifiedInsightItem[];
  insightType: InsightType;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  sentryRef: (node: HTMLElement | null) => void;
  onTopicClick?: (topic: string) => void;
}

type DayGroupData = {
  label: string;
  items: UnifiedInsightItem[];
};

export const InsightsSection = ({
  items,
  insightType,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  sentryRef,
  onTopicClick,
}: InsightsSectionProps) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'unified_ai_insights' });

  const dayGroups = useMemo((): DayGroupData[] => {
    const groups: Map<string, UnifiedInsightItem[]> = new Map();
    const labelMap: Map<string, string> = new Map();

    for (const item of items) {
      const date = toValidDate(item.conversation_timestamp);
      if (!date) continue;
      const dateKey = format(date, 'yyyy-MM-dd');

      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
        if (isToday(date)) {
          labelMap.set(dateKey, t('today'));
        } else if (isYesterday(date)) {
          labelMap.set(dateKey, t('yesterday'));
        } else {
          labelMap.set(dateKey, format(date, 'MMM d, yyyy'));
        }
      }
      groups.get(dateKey)!.push(item);
    }

    return Array.from(groups.entries()).map(([dateKey, groupItems]) => ({
      label: labelMap.get(dateKey)!,
      items: groupItems,
    }));
  }, [items, t]);

  const showEmptyState = !isLoading && dayGroups.length === 0;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (showEmptyState) {
    return insightType === 'alerts' ? <EmptyAlertsState /> : <EmptyInsightsState />;
  }

  return (
    <div className="space-y-4">
      {dayGroups.map((group) => (
        <DayGroup
          key={group.label}
          label={group.label}
          items={group.items}
          insightType={insightType}
          onTopicClick={onTopicClick}
        />
      ))}
      {(isFetchingNextPage || hasNextPage) && (
        <div ref={sentryRef} className="flex justify-center py-4">
          {isFetchingNextPage && <Spinner />}
        </div>
      )}
    </div>
  );
};
